import { FC, memo, useCallback, useRef } from 'react';
import cn from 'classnames';
import { Spin } from 'antd';
import type {
  SorterResult,
  FilterValue,
  TableCurrentDataSource,
} from 'antd/es/table/interface';
import { ColumnsType } from 'antd/lib/table';
import { OutgoingCallsRequest } from '../../../store/reducers/OutgoingCallsSlice';
import {
  CurrentOutgoingCalls,
  OutgoingCallsTablet,
} from '../../../models/OutgoingCalls/OutgoingCalls';
import { TablePaginationConfig } from 'antd/es/table/interface';
import styles from './style.module.css';
import tableDefaults from '../../shared/antTableDefaults.module.css';
import { useTableHeight } from '../../../hooks/components/useTableHeight';
import { useCallDetailsModal } from '../../../hooks/components/useCallDetailsModal/useCallDetailsModal';
import {
  CallDetailsModal,
  JOURNAL_TASK_STATUS_CONFIG,
} from '../CallDetailsModal/CallDetailsModal';
import { CallAttemptCard } from '../CallDetailsModal/CallAttemptCard/CallAttemptCard';
import { callAttemptStateLabel } from '../../../models/CallAttempt/CallAttempt';
import { TableFooter } from '../../TableFooter';
import { createDetailsColumn } from '../CallDetailsModal/DetailsColumn';
import { NotificationType } from '../../../models/enums/NotificationType';
import { parseOutgoingDoctorInfo } from '../../../hooks/components/useOutgoingCalls/useOutgoingCallsState/outgoingDoctorInfo';
import { LoadingTable } from '../../shared/Loading';
import { TableResults } from '../../shared/TableResults';

interface Props {
  pageSize: number;
  totalCount: number;
  total: number;
  onChange: (page: number, pageItemCount: number) => void;
  callRequest: OutgoingCallsRequest;
  allCalls: CurrentOutgoingCalls[];
  onTableChange?:
    | ((
        pagination: TablePaginationConfig,
        filters: Record<string, FilterValue | null>,
        sorter:
          | SorterResult<OutgoingCallsTablet>
          | SorterResult<OutgoingCallsTablet>[],
        extra: TableCurrentDataSource<OutgoingCallsTablet>,
      ) => void)
    | undefined;
  COLUMNS: ColumnsType<OutgoingCallsTablet>;
  divider: { height: number; backgroundColor: string; margin: string };
  handlePageSizeChange: (value: string) => void;
  filtersCollapsed?: boolean;
  scenarioLabelByType?: Record<string, string>;
  resultLabelByValue?: Readonly<Record<string, string>>;
  loading?: boolean;
  hasLoadError?: boolean;
}

export const OutgoingCalls: FC<Props> = memo(
  ({
    pageSize,
    onChange,
    callRequest,
    onTableChange,
    total,
    allCalls,
    COLUMNS,
    divider,
    handlePageSizeChange,
    filtersCollapsed,
    scenarioLabelByType = {},
    resultLabelByValue = {},
    loading = false,
    hasLoadError = false,
  }: Props) => {
    const tableContainerRef = useRef<HTMLDivElement | null>(null);

    const { tableHeight, containerHeight, isTabletScroll } = useTableHeight({
      tableContainerRef,
      items: allCalls,
      extraDeps: [filtersCollapsed],
    });

    const tabletScroll = isTabletScroll ? { y: tableHeight } : undefined;

    const {
      isOpen,
      openModal,
      closeModal,
      callAttempts,
      isLoadingAttempts,
      loadingError,
      callType,
      audioUrls,
      loadAudio,
      transcriptions,
      loadTranscription,
      transcriptionLoading,
      transcriptionAvailability,
      callDetails,
      isLoadingDetails,
      detailsError,
    } = useCallDetailsModal();

    const selectedRecord = useRef<OutgoingCallsTablet | null>(null);

    const handleOpenDetails = useCallback(
      (record: OutgoingCallsTablet) => {
        selectedRecord.current = record;
        // Prefer the canonical TaskType (`record.type`) when available.
        // Legacy records only have `notificationType` (e.g. 'ConfirmVisit')
        // which must be mapped to the TaskType enum (e.g. 'OutboundConfirmVisit').
        const taskType =
          record.type ??
          (record.notificationType
            ? `Outbound${record.notificationType}`
            : undefined);
        openModal({
          taskId: record.id,
          callType: 'outgoing',
          taskType,
          taskTypeLabel: taskType ? scenarioLabelByType[taskType] : undefined,
        });
      },
      [openModal, scenarioLabelByType],
    );

    const columnsWithDetails = [
      ...COLUMNS,
      createDetailsColumn<OutgoingCallsTablet>(handleOpenDetails),
    ];

    const rec = selectedRecord.current;
    const doctorInfo = rec
      ? parseOutgoingDoctorInfo(rec.surnameDoctor ?? '')
      : { doctorName: null, doctorSpecialization: null };

    return (
      <div
        ref={tableContainerRef}
        className={cn(
          styles.OutgoingCalls,
          tableDefaults.defaultTableContainer,
        )}
      >
        <TableResults
          isEmpty={!hasLoadError && allCalls.length === 0 && total === 0}
          loading={loading}
        >
          <div style={{ height: `${containerHeight}px` }}>
            <LoadingTable<OutgoingCallsTablet>
              columns={columnsWithDetails}
              tableLayout="fixed"
              percentageColumnWidths
              className={cn(
                styles.table,
                styles.stickyHeader,
                tableDefaults.defaultTable,
              )}
              rowClassName={styles.customRow}
              onChange={onTableChange}
              pagination={false}
              rowKey={(record) =>
                record.indexId !== undefined ? record.indexId : ''
              }
              showSorterTooltip={false}
              dataSource={allCalls.map((call, index) => ({
                ...call,
                index,
              }))}
              scroll={tabletScroll}
              loading={loading}
            />
          </div>
          <div className={styles.divider} style={divider} />

          <TableFooter
            pageIndex={callRequest.PageIndex}
            pageSize={pageSize}
            total={total}
            onPageChange={onChange}
            onPageSizeChange={handlePageSizeChange}
            disabled={loading}
          />
        </TableResults>

        <CallDetailsModal
          open={isOpen}
          onClose={closeModal}
          taskStatus={rec?.status ?? ''}
          patientPhone={rec?.phone ?? ''}
          patientFullName={rec?.surname ?? null}
          clinicName={rec?.clinic ?? null}
          appointmentDate={rec?.appointmentDate ?? null}
          doctorName={doctorInfo.doctorName}
          doctorSpecialization={doctorInfo.doctorSpecialization}
          callType="outgoing"
          taskResult={rec?.result ?? null}
          resultLabelByValue={resultLabelByValue}
          scenario={
            NotificationType.find(
              (type) => type.value === rec?.notificationType,
            )?.text ??
            rec?.notificationType ??
            null
          }
          details={callDetails}
          isLoadingDetails={isLoadingDetails}
          detailsError={detailsError}
          statusConfigOverride={JOURNAL_TASK_STATUS_CONFIG}
        >
          {isLoadingAttempts ? (
            <div style={{ textAlign: 'center', padding: '24px' }}>
              <Spin />
            </div>
          ) : loadingError ? (
            <div
              style={{ textAlign: 'center', padding: '24px', color: '#ff4d4f' }}
            >
              {loadingError}
            </div>
          ) : callAttempts.length === 0 ? (
            <div
              style={{ textAlign: 'center', padding: '24px', color: '#8c8c8c' }}
            >
              Для данного задания отсутствуют записи о попытках звонков
            </div>
          ) : (
            [...callAttempts]
              .sort(
                (a, b) =>
                  new Date(a.callDateTime).getTime() -
                  new Date(b.callDateTime).getTime(),
              )
              .map((attempt, index, arr) => {
                const stateLabel = callAttemptStateLabel(
                  attempt.callAttemptState,
                );
                const hasRecording = Boolean(attempt.recording);
                return (
                  <CallAttemptCard
                    key={attempt.id}
                    recordingId={attempt.id}
                    recordingPath={attempt.recording}
                    callDateTime={attempt.callDateTime}
                    callState={stateLabel}
                    hasRecording={hasRecording}
                    audioUrl={audioUrls[attempt.id] ?? null}
                    onLoadAudio={() => loadAudio(attempt.id)}
                    transcription={transcriptions[attempt.id] ?? null}
                    isTranscriptionLoading={
                      transcriptionLoading[attempt.id] ?? false
                    }
                    onLoadTranscription={() => loadTranscription(attempt.id)}
                    hasTranscription={
                      transcriptionAvailability[attempt.id] ?? false
                    }
                    attemptNumber={index + 1}
                    isLast={index === arr.length - 1}
                  />
                );
              })
          )}
        </CallDetailsModal>
      </div>
    );
  },
);

OutgoingCalls.displayName = 'OutgoingCalls';
